import React, { ChangeEvent } from "react";
import { useFormContext } from "react-hook-form";
import CField, { CFieldInputProps } from "$forms/CField";
import CompanySelect from "$forms/CompanySelect";
import AddressSelect from "$forms/AddressSelect";
import PostalCode from "$forms/PostalCode";
import { CompanyType, Warehouse } from "$types";
import { useEntrepotForm } from "../context/EntrepotFormContext";
import {
  nameRegexp,
  NAME_ERROR_MSG,
  normalizeName,
  unsensitiveIsSameString,
} from "$utils/utils";
import { Text } from "$components";
import useAuth from "$hooks/useAuth";
import { queryClient } from "$utils/queryClient";
import { EntrepotService } from "$services";
import useOrder from "$hooks/useOrder";
import { CacheInvalidationAction } from "$utils/cache";

export const EntrepotFormBasicInfo: React.FC = () => {
  const {
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext<Warehouse>();
  const { classes } = useOrder();
  const { isAdmin } = useAuth();
  const {
    entrepot,
    parentCompanyId,
    fuzzySearchOnWarehouseName,
    matchingWarehouses,
  } = useEntrepotForm();

  const initialName = entrepot?.name;

  const country = watch("location.country");

  const allowedCompanyTypes = [
    CompanyType.LOGISTICIAN,
    CompanyType.CONTRIBUTOR,
    CompanyType.CLIENT,
  ];

  const getDockyards = async (newName) => {
    const filters = {
      ...{ name: newName },
    };
    try {
      const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
      let getWarehouses: any = await queryClient.getQueryData(queryKey);
      if (!getWarehouses) {
        getWarehouses = await queryClient.fetchQuery(
          queryKey,
          () => EntrepotService.getEntrepots(filters),
          { cacheTime: 5 * 60 * 1000 }
        );
        queryClient.setQueryData(queryKey, getWarehouses);
      }
      return getWarehouses?.data || [];
    } catch (error) {
      return null;
    }
  };

  const validateName = async (newName: string) => {
    // Insensitive to case and accents comparison + initial name is valid
    const docyard = await getDockyards(newName);
    const doesACompanyExists = docyard
      ? docyard.findIndex((warehouse) =>
          unsensitiveIsSameString(warehouse.name, newName)
        ) !== -1
      : false;

    const isNameUnchanged = initialName
      ? unsensitiveIsSameString(initialName, newName)
      : false;

    return !doesACompanyExists || isNameUnchanged;
  };

  const addressOnChange = (data) => {
    if (Array.isArray(data)) {
      let street = "";

      data.forEach((elem: any) => {
        if (elem.types.includes("postal_code")) {
          setValue("location.cp", elem.long_name);
        }
        if (elem.types.includes("country")) {
          setValue("location.country", elem.long_name);
        }
        if (elem.types.includes("locality")) {
          setValue("location.city", elem.long_name);
        }
        if (elem.types.includes("street_number")) {
          if (street === "") {
            street += elem.long_name;
          } else {
            street = `${elem.long_name}${street}`;
          }
        }
        if (elem.types.includes("route")) {
          street += ` ${elem.long_name}`;
        }
      });
      setValue("location.adress", street);
    } else {
      setValue("location.adress", data);
    }
  };

  return (
    <>
      {(!watch("parentCompany") || entrepot?._id) && (
        <div className={classes.newFormFieldsRow}>
          <CField
            control={control}
            name="parentCompany"
            error={errors.parentCompany}
            label="Entreprise parente"
            required
            disabled={!!parentCompanyId}
          >
            {(props: CFieldInputProps) => (
              <CompanySelect {...props} companyTypes={allowedCompanyTypes} />
            )}
          </CField>
        </div>
      )}

      <div className={classes.newFormFieldsRow}>
        <CField
          label="Nom"
          disabled={!isAdmin()}
          control={control}
          name="name"
          required
          error={errors.name}
          rules={{
            pattern: {
              value: nameRegexp,
              message: NAME_ERROR_MSG,
            },
            validate: (v) =>
              validateName(v) || "Un entrepôt avec ce nom existe déjà",
          }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setValue("name", normalizeName(e.target.value));
            if (e.target.value) {
              trigger("name");
            }
          }}
          onBlur={(e: ChangeEvent<HTMLInputElement>) => {
            fuzzySearchOnWarehouseName(e.target.value);
          }}
        />
      </div>

      {matchingWarehouses?.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {matchingWarehouses?.map((matchingWarehouse) => {
            return (
              <Text key={matchingWarehouse._id} type="warning">
                {`Un ou plusieurs entrepôts portent un nom similaire : [${matchingWarehouse.name}].`}
              </Text>
            );
          })}
          <span>
            Nous vous conseillons de consulter la liste des entrepôts afin de ne
            pas créer de doublons.
          </span>
        </div>
      )}

      <div className={classes.newFormFieldsRow}>
        <CField
          control={control}
          label="Adresse"
          disabled={!isAdmin()}
          name="location.adress"
          error={errors.location?.adress}
          required
        >
          {(props) => (
            <AddressSelect
              value={props.value}
              required
              onChange={addressOnChange}
            />
          )}
        </CField>
      </div>

      <div className={classes.newFormFieldsRow}>
        <CField
          label="Complément d'adresse"
          disabled={!isAdmin()}
          control={control}
          name="location.complement"
          error={errors.location?.complement}
        />
      </div>

      <div className={classes.newFormFieldsRow}>
        <div className={classes.newFormFieldSmallCol}>
          <PostalCode
            disabled={!isAdmin()}
            control={control}
            country={country}
            name="location.cp"
            required
            error={errors.location?.cp}
          />
        </div>
        <div className={classes.newFormFieldsCol}>
          <CField
            label="Ville"
            disabled={!isAdmin()}
            control={control}
            name="location.city"
            required
            error={errors.location?.city}
          />
        </div>
        <div className={classes.newFormFieldSmallCol}>
          <CField
            control={control}
            label="Pays"
            disabled={!isAdmin()}
            name="location.country"
            error={errors.location?.country}
            required
          />
        </div>
      </div>
    </>
  );
};
